import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";

import type { Corner } from "../lib/corners";
import { projectionOrigin, projectLap, type Point } from "../lib/geo";
import { buildTrack } from "../lib/track";
import {
  accelColour,
  ACCEL_LEGEND,
  pedalColour,
  PEDAL_LEGEND,
  type AlignedLap,
  type TrackColourMode,
} from "../lib/trace";

interface Props {
  laps: AlignedLap[];
  reference: AlignedLap;
  mode: TrackColourMode;
  /** Index into the shared distance grid, or null when nothing is hovered. */
  hoverIndex: number | null;
  /** Grid range to zoom to, or null for the whole lap. */
  focus: { startIndex: number; endIndex: number } | null;
  corners: Corner[];
  distanceStep: number;
}

interface Run {
  colour: string;
  points: Point[];
}

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A lap is drawn as wide as the car that drove it, because the whole point of zooming
 * into a corner is to see how far apart two lines actually were. Sizing strokes as a
 * fraction of the circuit's bounding box, as this used to, drew the car about ten
 * metres wide and swallowed every difference between laps.
 */
const CAR_WIDTH_M = 1.9;

/** Zoomed all the way out a car is sub-pixel, so the width also has a floor. */
const MIN_LINE_PX = 2;
/** The casing is an outline for contrast, so this one is only ever in pixels. */
const CASING_PX = 2;
const EDGE_LINE_M = 0.3;

/** Breathing room around the drawn lines, in metres. */
const PADDING_M = 10;

/** Two car lengths in, three times the circuit out. */
const MIN_VIEW_M = 25;
const MAX_VIEW_FACTOR = 3;

/**
 * Draws the circuit and the laps driven around it.
 *
 * The road comes from `lib/track`, which rebuilds the centre line and both edges from
 * what LMU records about them, and places every lap on it by its own distance from the
 * middle — so two lines are drawn exactly as far from the kerb as each car ran. LMU
 * places its circuits at a synthetic lat/lon rather than their real location, so
 * absolute coordinates are meaningless, but the shape and the relative distances are
 * not. A session recorded without those channels falls back to the raw GPS lines and
 * draws no road at all, since anything else would be invented.
 *
 * SVG rather than canvas: this is a few dozen polylines that only redraw when the laps
 * change, and the hover marker is a single element React can move on its own.
 */
export function TrackMap({
  laps,
  reference,
  mode,
  hoverIndex,
  focus,
  corners,
  distanceStep,
}: Props) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const size = useElementSize(svgRef);

  const origin = useMemo(() => projectionOrigin(reference), [reference]);

  const track = useMemo(
    () => (origin ? buildTrack(laps, origin, distanceStep) : null),
    [laps, origin, distanceStep],
  );

  const projected = useMemo(
    () =>
      origin
        ? laps.map((lap) => ({
            lap,
            points: track?.lines.get(lap.key) ?? projectLap(lap, origin),
          }))
        : [],
    [laps, origin, track],
  );

  // What the view has to fit: the road where there is one, or else just the lines.
  const extents = useMemo(
    () => [
      ...projected.map((entry) => entry.points),
      ...(track ? [track.left, track.right] : []),
    ],
    [projected, track],
  );

  const referencePoints = useMemo(
    () => projected.find((entry) => entry.lap.key === reference.key)?.points ?? null,
    [projected, reference],
  );

  const runs = useMemo(
    () => (referencePoints ? colourRuns(reference, referencePoints, mode) : []),
    [reference, referencePoints, mode],
  );

  const fullBounds = useMemo(() => boundsOf(extents, null), [extents]);

  const focusBounds = useMemo(
    () => (focus ? boundsOf(extents, focus) : fullBounds),
    [extents, focus, fullBounds],
  );

  // Wheel and drag write here; selecting a range on the charts or clicking a corner
  // clears it, so the two ways of moving the map do not fight each other.
  const [panned, setPanned] = useState<Box | null>(null);
  useEffect(() => setPanned(null), [focusBounds]);

  const view = panned ?? focusBounds;
  const box = fitToViewport(view, size);
  // Until the observer has measured the element, guess a width rather than draw
  // metre-sized labels for one frame.
  const metresPerPixel = box ? box.width / (size.width || 600) : 1;

  // Read by the wheel and pointer handlers, which are registered once.
  const state = useRef({ box, fullBounds, metresPerPixel });
  state.current = { box, fullBounds, metresPerPixel };

  const zoom = useCallback((factor: number, atX: number, atY: number) => {
    const { box: current, fullBounds: limit } = state.current;
    if (!current || !limit) return;

    const widest = Math.max(limit.width, limit.height) * MAX_VIEW_FACTOR;
    const width = clamp(current.width * factor, MIN_VIEW_M, widest);
    const scale = width / current.width;
    const height = current.height * scale;

    setPanned({
      // The point under the cursor is the one that must not move.
      x: current.x + (current.width - width) * atX,
      y: current.y + (current.height - height) * atY,
      width,
      height,
    });
  }, []);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // React registers onWheel passively, so preventing the page from scrolling
    // underneath the map needs a listener of our own.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      // Firefox reports lines rather than pixels.
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      zoom(
        Math.exp(clamp(delta, -200, 200) * 0.002),
        (event.clientX - rect.left) / rect.width,
        (event.clientY - rect.top) / rect.height,
      );
    };

    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [zoom]);

  const drag = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    if (event.button !== 0) return;
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    const held = drag.current;
    if (!held || held.pointerId !== event.pointerId) return;
    const { box: current, metresPerPixel: scale } = state.current;
    if (!current) return;

    setPanned({
      ...current,
      x: current.x - (event.clientX - held.x) * scale,
      y: current.y - (event.clientY - held.y) * scale,
    });
    drag.current = { ...held, x: event.clientX, y: event.clientY };
  }

  function endDrag(event: ReactPointerEvent<SVGSVGElement>) {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
  }

  if (!referencePoints || !fullBounds || !box) {
    return (
      <p className="py-12 text-center text-sm text-zinc-500">
        This session has no GPS channel, so the track cannot be drawn.
      </p>
    );
  }

  const line = Math.max(CAR_WIDTH_M, MIN_LINE_PX * metresPerPixel);
  // Eight car-width strokes side by side would merge back into one band, and the
  // point of this mode is the gap between them.
  const lapLine = Math.max(CAR_WIDTH_M / 2, MIN_LINE_PX * metresPerPixel);
  // Labels and markers are sized on screen, not on the track, so they stay legible
  // at any zoom.
  const px = metresPerPixel;
  // Painted edge lines are a few tens of centimetres wide, but never thinner than a
  // pixel or they vanish when zoomed out.
  const edgeLine = Math.max(EDGE_LINE_M, px);

  const legend = mode === "accel" ? ACCEL_LEGEND : mode === "pedals" ? PEDAL_LEGEND : null;

  return (
    <div>
      <svg
        ref={svgRef}
        viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
        preserveAspectRatio="xMidYMid meet"
        className={`h-96 w-full touch-none select-none ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={() => setPanned(null)}
      >
        {track && (
          <g>
            {ribbons(track.left, track.right).map((points, index) => (
              <polygon key={index} points={points} fill="#27272a" />
            ))}
            {[track.left, track.right].flatMap((edge, side) =>
              segments(edge).map((points, index) => (
                <polyline
                  key={`${side}-${index}`}
                  points={points}
                  fill="none"
                  stroke="#d4d4d8"
                  strokeOpacity={0.6}
                  strokeWidth={edgeLine}
                  strokeLinejoin="round"
                />
              )),
            )}
          </g>
        )}

        {/* A dark outline keeps each line legible against the road and stops adjacent
            colours from bleeding into each other. */}
        {(mode === "laps" ? projected : [{ lap: reference, points: referencePoints }]).map(
          ({ lap, points }) =>
            points ? (
              <polyline
                key={lap.key}
                points={pathOf(points)}
                fill="none"
                stroke="#09090b"
                strokeOpacity={0.8}
                strokeWidth={(mode === "laps" ? lapLine : line) + CASING_PX * px}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ) : null,
        )}

        {mode === "laps"
          ? projected.map(({ lap, points }) =>
              points ? (
                <polyline
                  key={lap.key}
                  points={pathOf(points)}
                  fill="none"
                  stroke={lap.colour}
                  strokeWidth={lapLine}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity={0.9}
                />
              ) : null,
            )
          : runs.map((run, index) => (
              <polyline
                key={index}
                points={run.points.map((point) => `${point.x},${point.y}`).join(" ")}
                fill="none"
                stroke={run.colour}
                strokeWidth={line}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}

        {corners.map((corner) => {
          const at = referencePoints[corner.apexIndex];
          if (!at) return null;
          const active =
            focus !== null &&
            corner.apexIndex >= focus.startIndex &&
            corner.apexIndex <= focus.endIndex;
          return (
            <g key={corner.number}>
              <circle
                cx={at.x}
                cy={at.y}
                r={px * 11}
                fill="#09090b"
                fillOpacity={0.85}
                stroke={active ? "#fafafa" : "#71717a"}
                strokeWidth={px * 1.5}
              />
              <text
                x={at.x}
                y={at.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill={active ? "#fafafa" : "#a1a1aa"}
                fontSize={px * 11}
                fontWeight={600}
              >
                {corner.number}
              </text>
            </g>
          );
        })}

        {startMarker(referencePoints, px)}

        {(mode === "laps" ? projected : [{ lap: reference, points: referencePoints }]).map(
          ({ lap, points }) => {
            const at = markerAt(points, hoverIndex);
            if (!at) return null;
            return (
              <g key={lap.key}>
                <circle cx={at.x} cy={at.y} r={px * 9} fill={lap.colour} fillOpacity={0.2} />
                <circle
                  cx={at.x}
                  cy={at.y}
                  r={px * 4}
                  fill={mode === "laps" ? lap.colour : "#fafafa"}
                  stroke="#18181b"
                  strokeWidth={px * 1.2}
                />
              </g>
            );
          },
        )}
      </svg>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {legend ? (
          <ul className="flex flex-wrap gap-x-3 gap-y-1">
            {legend.map((band) => (
              <li
                key={band.label}
                className="flex items-center gap-1.5 text-[11px] text-zinc-400"
              >
                <span
                  className="h-2 w-3 rounded-sm"
                  style={{ backgroundColor: band.colour }}
                />
                {band.key ? t(`trackMap.bands.${band.key}`) : band.label}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11px] text-zinc-500">{t("trackMap.lapColours")}</p>
        )}
        <ScaleBar metresPerPixel={metresPerPixel} />
      </div>
      <p className="mt-1 text-[11px] text-zinc-600">{t("trackMap.controls")}</p>
    </div>
  );
}

/**
 * Lines a metre apart only mean something if you can tell what a metre is on screen,
 * and the scale changes with every zoom.
 */
function ScaleBar({ metresPerPixel }: { metresPerPixel: number }) {
  const metres = niceRound(metresPerPixel * 80);
  return (
    <div className="flex items-center gap-2 text-[11px] tabular-nums text-zinc-500">
      <span
        className="h-2 border-x border-b border-zinc-600"
        style={{ width: `${metres / metresPerPixel}px` }}
      />
      {metres < 1 ? `${metres.toFixed(1)} m` : `${metres.toFixed(0)} m`}
    </div>
  );
}

/** 1, 2, 5, 10, 20, 50 … so the bar always reads as a round number. */
function niceRound(value: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(value, 0.1)));
  const digits = value / magnitude;
  const rounded = digits >= 5 ? 5 : digits >= 2 ? 2 : 1;
  return rounded * magnitude;
}

function useElementSize(ref: RefObject<Element | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

/**
 * Grow the box to the element's aspect ratio.
 *
 * `preserveAspectRatio` would letterbox it instead, which draws the same picture but
 * leaves the viewBox describing an area that is not the one on screen — and every
 * pointer position and stroke width here is derived from exactly that.
 */
function fitToViewport(box: Box | null, size: { width: number; height: number }): Box | null {
  if (!box) return null;
  if (size.width <= 0 || size.height <= 0) return box;

  const scale = Math.max(box.width / size.width, box.height / size.height);
  const width = size.width * scale;
  const height = size.height * scale;
  return {
    x: box.x - (width - box.width) / 2,
    y: box.y - (height - box.height) / 2,
    width,
    height,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function pathOf(points: Array<Point | null>): string {
  return points
    .filter((point): point is Point => point !== null)
    .map((point) => `${point.x},${point.y}`)
    .join(" ");
}

/** Unbroken stretches of a line, so a gap in the data is left as a gap. */
function segments(points: Array<Point | null>): string[] {
  const out: string[] = [];
  let run: Point[] = [];
  for (const point of [...points, null]) {
    if (point) {
      run.push(point);
      continue;
    }
    if (run.length >= 2) out.push(run.map((at) => `${at.x},${at.y}`).join(" "));
    run = [];
  }
  return out;
}

/** The road surface between two edges, one polygon per unbroken stretch. */
function ribbons(left: Array<Point | null>, right: Array<Point | null>): string[] {
  const out: string[] = [];
  let start = -1;
  for (let index = 0; index <= left.length; index += 1) {
    const present = index < left.length && left[index] !== null && right[index] !== null;
    if (present && start < 0) start = index;
    if (present || start < 0) continue;
    if (index - start >= 2) {
      const outbound = left.slice(start, index) as Point[];
      const inbound = (right.slice(start, index) as Point[]).reverse();
      out.push([...outbound, ...inbound].map((at) => `${at.x},${at.y}`).join(" "));
    }
    start = -1;
  }
  return out;
}

function startMarker(points: Array<Point | null>, px: number) {
  const first = points.find((point): point is Point => point !== null);
  if (!first) return null;
  return (
    <circle
      cx={first.x}
      cy={first.y}
      r={px * 5}
      fill="none"
      stroke="#fafafa"
      strokeWidth={px * 2}
    />
  );
}

function colourRuns(
  lap: AlignedLap,
  points: Array<Point | null>,
  mode: TrackColourMode,
): Run[] {
  const throttle = lap.channels.throttle ?? [];
  const brake = lap.channels.brake ?? [];
  const gLong = lap.channels.g_long ?? [];

  const colourAt = (index: number) =>
    mode === "accel"
      ? accelColour(gLong[index] ?? null)
      : pedalColour(throttle[index] ?? null, brake[index] ?? null);

  // Consecutive points sharing a colour become one polyline. Each run repeats the next
  // run's first point so the line stays visually unbroken at the handover.
  const runs: Run[] = [];
  let current: Run | null = null;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    if (!point) continue;
    const colour = colourAt(index);
    if (!current || current.colour !== colour) {
      current?.points.push(point);
      current = { colour, points: [point] };
      runs.push(current);
    } else {
      current.points.push(point);
    }
  }
  return runs;
}

function boundsOf(
  lapPoints: Array<Array<Point | null> | null>,
  focus: { startIndex: number; endIndex: number } | null,
): Box | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const points of lapPoints) {
    if (!points) continue;
    const from = focus ? Math.max(0, focus.startIndex) : 0;
    const to = focus ? Math.min(points.length - 1, focus.endIndex) : points.length - 1;
    for (let index = from; index <= to; index += 1) {
      const point = points[index];
      if (!point) continue;
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  if (!Number.isFinite(minX)) return null;

  // A corner that runs almost straight in one axis would otherwise get a degenerate
  // box.
  const width = Math.max(maxX - minX + PADDING_M * 2, MIN_VIEW_M);
  const height = Math.max(maxY - minY + PADDING_M * 2, MIN_VIEW_M);
  return {
    x: (minX + maxX) / 2 - width / 2,
    y: (minY + maxY) / 2 - height / 2,
    width,
    height,
  };
}

/**
 * The hovered grid point may be one the GPS channel did not sample, so look a little
 * either side rather than dropping the marker.
 */
function markerAt(
  points: Array<Point | null> | null,
  hoverIndex: number | null,
): Point | null {
  if (!points || hoverIndex === null) return null;
  for (let radius = 0; radius <= 4; radius += 1) {
    const before = points[hoverIndex - radius];
    if (before) return before;
    const after = points[hoverIndex + radius];
    if (after) return after;
  }
  return null;
}
